import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Topbar } from "@/components/parcelio/topbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { directoryApi, type CorporateAccount, type CorporateMember } from "@/lib/api";
import { formatNaira } from "@/lib/mock-data";
import { Plus, Search, Building2, Users, Pencil, UserPlus, Trash2, Check, ChevronsUpDown, LayoutDashboard, Info } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { LoadingBlock } from "@/components/ui/spinner";
import { useAuth } from "@/stores/auth";

type CorpStatus = CorporateAccount["status"];

function generatePassword() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%^&*";
  const bytes = new Uint32Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => chars[b % chars.length]).join("");
}

const INDUSTRIES = [
  "Agriculture", "Automotive", "Construction", "Consumer Goods", "E-commerce",
  "Education", "Energy & Utilities", "Fashion & Apparel", "Financial Services",
  "Food & Beverage", "Healthcare", "Hospitality", "Import & Export",
  "Insurance", "Logistics & Transportation", "Manufacturing", "Media & Entertainment",
  "Oil & Gas", "Pharmaceuticals", "Real Estate", "Retail", "Technology",
  "Telecommunications", "Warehousing",
];

function IndustryCombobox({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const filtered = INDUSTRIES.filter((i) => i.toLowerCase().includes(query.toLowerCase()));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between font-normal">
          {value || "Select industry…"}
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search industry…" value={query} onValueChange={setQuery} />
          <CommandList>
            <CommandEmpty>
              {query.trim() ? (
                <button
                  type="button"
                  className="w-full px-2 py-1.5 text-left text-sm hover:bg-accent"
                  onClick={() => { onChange(query.trim()); setOpen(false); }}
                >
                  Use "{query.trim()}"
                </button>
              ) : "No industry found."}
            </CommandEmpty>
            <CommandGroup>
              {filtered.map((i) => (
                <CommandItem key={i} value={i} onSelect={() => { onChange(i); setOpen(false); }}>
                  <Check className={cn("h-4 w-4", value === i ? "opacity-100" : "opacity-0")} />
                  {i}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

const statusTone: Record<string, string> = {
  active: "bg-success/10 text-success border-success/20",
  trial: "bg-info/10 text-info border-info/20",
  churned: "bg-muted text-muted-foreground border-border",
};

type Form = {
  company: string;
  industry: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  status: CorpStatus;
};
const empty: Form = { company: "", industry: "", contact_name: "", contact_email: "", contact_phone: "", status: "trial" };

// Owner (admin for the company) is only set once, at creation — editing a
// corporate never touches this.
type OwnerForm = { name: string; email: string; password: string };
const emptyOwner: OwnerForm = { name: "", email: "", password: "" };

export default function CorporatesPage() {
  const token = useAuth((s) => s.session?.accessToken);
  const [items, setItems] = useState<CorporateAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<CorporateAccount | null>(null);
  const [form, setForm] = useState<Form>(empty);
  const [owner, setOwner] = useState<OwnerForm>(emptyOwner);
  const [viewing, setViewing] = useState<CorporateAccount | null>(null);
  const [newMember, setNewMember] = useState({ name: "", email: "", role: "member", password: "" });
  const [newDriver, setNewDriver] = useState({ name: "", email: "", phone: "" });
  const [detailMember, setDetailMember] = useState<CorporateMember | null>(null);
  const [editMember, setEditMember] = useState<CorporateMember | null>(null);
  const [editMemberForm, setEditMemberForm] = useState({ name: "", email: "", role: "member", password: "" });

  useEffect(() => {
    let cancelled = false;
    directoryApi
      .corporates(token)
      .then((data) => {
        if (!cancelled) setItems(data);
      })
      .catch(() => toast.error("Failed to load corporate accounts"))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const filtered = useMemo(() => items.filter((c) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (
      c.company.toLowerCase().includes(s) ||
      (c.industry?.toLowerCase().includes(s) ?? false) ||
      (c.contact_email?.toLowerCase().includes(s) ?? false)
    );
  }), [items, q]);

  const openCreate = () => { setEditing(null); setForm(empty); setOwner(emptyOwner); setOpen(true); };
  const openEdit = (c: CorporateAccount) => {
    setEditing(c);
    setForm({
      company: c.company,
      industry: c.industry ?? "",
      contact_name: c.contact_name ?? "",
      contact_email: c.contact_email ?? "",
      contact_phone: c.contact_phone ?? "",
      status: c.status,
    });
    setOpen(true);
  };
  const save = async () => {
    if (!form.company.trim() || !form.contact_email.trim()) return toast.error("Company and contact email required");
    if (!editing) {
      if (!owner.name.trim() || !owner.email.trim()) return toast.error("Owner name and email required");
      if (owner.password.length < 12) return toast.error("Owner password must be at least 12 characters");
    }
    try {
      if (editing) {
        const updated = await directoryApi.updateCorporate(editing.id, form, token);
        setItems((cur) => cur.map((c) => (c.id === editing.id ? updated : c)));
        toast.success("Corporate updated");
      } else {
        const created = await directoryApi.createCorporate({ ...form, owner }, token);
        setItems((cur) => [created, ...cur]);
        toast.success("Corporate onboarded");
      }
      setOpen(false);
    } catch {
      toast.error(editing ? "Could not update corporate" : "Could not onboard corporate");
    }
  };
  const addMember = async () => {
    if (!viewing || !newMember.name.trim() || !newMember.email.trim()) return toast.error("Name and email required");
    if (newMember.password && newMember.password.length < 12) return toast.error("Password must be at least 12 characters");
    try {
      const updated = await directoryApi.addCorporateMember(viewing.id, {
        name: newMember.name,
        email: newMember.email,
        role: newMember.role,
        password: newMember.password || undefined,
      }, token);
      setItems((cur) => cur.map((c) => (c.id === viewing.id ? updated : c)));
      setViewing(updated);
      setNewMember({ name: "", email: "", role: "member", password: "" });
      toast.success("Member invited");
    } catch {
      toast.error("Could not invite member");
    }
  };
  const addDriver = async () => {
    if (!viewing || !newDriver.name.trim() || (!newDriver.email.trim() && !newDriver.phone.trim())) {
      return toast.error("Name and email or phone required");
    }
    try {
      await directoryApi.createDriver({
        full_name: newDriver.name,
        email: newDriver.email || undefined,
        phone: newDriver.phone || undefined,
        corporate_account_id: viewing.id,
      }, token);
      setNewDriver({ name: "", email: "", phone: "" });
      toast.success("Driver added to fleet");
    } catch {
      toast.error("Could not add driver");
    }
  };
  const openEditMember = (m: CorporateMember) => {
    setEditMember(m);
    setEditMemberForm({ name: m.name, email: m.email, role: m.role, password: "" });
  };
  const saveMember = async () => {
    if (!viewing || !editMember) return;
    if (!editMemberForm.name.trim() || !editMemberForm.email.trim()) return toast.error("Name and email required");
    if (editMemberForm.password && editMemberForm.password.length < 12) return toast.error("Password must be at least 12 characters");
    try {
      const updated = await directoryApi.updateCorporateMember(viewing.id, editMember.id, {
        name: editMemberForm.name,
        email: editMemberForm.email,
        role: editMember.role === "owner" ? undefined : editMemberForm.role,
        password: editMemberForm.password || undefined,
      }, token);
      setItems((cur) => cur.map((c) => (c.id === viewing.id ? updated : c)));
      setViewing(updated);
      setEditMember(null);
      toast.success("Member updated");
    } catch {
      toast.error("Could not update member");
    }
  };
  const removeMember = async (memberId: string) => {
    if (!viewing) return;
    try {
      const updated = await directoryApi.removeCorporateMember(viewing.id, memberId, token);
      setItems((cur) => cur.map((c) => (c.id === viewing.id ? updated : c)));
      setViewing(updated);
      toast.success("Member removed");
    } catch {
      toast.error("Could not remove member");
    }
  };

  return (
    <>
      <Topbar
        title="Corporate accounts"
        subtitle={`${items.length} companies · ${items.reduce((s, c) => s + c.members.length, 0)} member seats`}
        actions={<Button size="sm" onClick={openCreate}><Plus className="mr-1.5 h-4 w-4" /> Onboard corporate</Button>}
      />
      <div className="flex-1 space-y-4 p-4 md:p-6">
        <div className="surface-card">
          <div className="flex items-center gap-3 border-b p-4">
            <div className="relative max-w-sm flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search company or industry…" className="pl-9" />
            </div>
          </div>
          {loading ? (
            <LoadingBlock label="Loading corporate accounts…" />
          ) : (
            <div className="grid gap-4 p-4 md:grid-cols-2 xl:grid-cols-3">
              {filtered.map((c) => (
                <article key={c.id} className="rounded-xl border p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-soft text-primary">
                      <Building2 className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between">
                        <h3 className="truncate font-semibold">{c.company}</h3>
                        <span className={cn("shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-medium capitalize", statusTone[c.status] ?? "bg-muted text-muted-foreground border-border")}>{c.status}</span>
                      </div>
                      <div className="text-xs text-muted-foreground">{c.industry ?? "—"} · since {c.since ? new Date(c.since).toLocaleDateString() : "—"}</div>
                      {c.contact_name ? <div className="truncate text-xs text-muted-foreground">Contact: {c.contact_name}</div> : null}
                    </div>
                  </div>
                  <dl className="mt-3 grid grid-cols-3 gap-2 rounded-lg bg-muted/40 p-3 text-center">
                    <div><dt className="text-[10px] uppercase tracking-wider text-muted-foreground">Members</dt><dd className="mt-0.5 font-semibold">{c.members.length}</dd></div>
                    <div><dt className="text-[10px] uppercase tracking-wider text-muted-foreground">Monthly vol.</dt><dd className="mt-0.5 font-semibold">{c.monthly_volume.toLocaleString()}</dd></div>
                    <div><dt className="text-[10px] uppercase tracking-wider text-muted-foreground">Contract</dt><dd className="mt-0.5 truncate font-semibold">{formatNaira(c.contract_value_kobo / 100)}</dd></div>
                  </dl>
                  <div className="mt-3">
                    <Button size="sm" className="w-full" asChild>
                      <Link to={`/app/users/corporates/${c.id}`}><LayoutDashboard className="mr-1.5 h-3.5 w-3.5" /> Dashboard</Link>
                    </Button>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <Button variant="outline" size="sm" onClick={() => setViewing(c)}><Users className="mr-1 h-3.5 w-3.5" /> Members</Button>
                    <Button variant="outline" size="sm" onClick={() => openEdit(c)}><Pencil className="mr-1 h-3.5 w-3.5" /> Edit</Button>
                  </div>
                </article>
              ))}
              {filtered.length === 0 ? (
                <div className="col-span-full p-8 text-center text-sm text-muted-foreground">No corporate accounts match.</div>
              ) : null}
            </div>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.company}` : "Onboard corporate"}</DialogTitle>
            <DialogDescription>Company account can manage multiple member users.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div><Label>Company</Label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></div>
            <div><Label>Industry</Label><IndustryCombobox value={form.industry} onChange={(v) => setForm({ ...form, industry: v })} /></div>
            <div><Label>Primary contact</Label><Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></div>
            <div><Label>Contact email</Label><Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></div>
            <div><Label>Contact phone</Label><Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} /></div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v as CorpStatus })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="churned">Churned</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {!editing ? (
              <div className="rounded-lg border p-3">
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                  Owner (admin for this company)
                </div>
                <div className="grid gap-3">
                  <div><Label>Owner name</Label><Input value={owner.name} onChange={(e) => setOwner({ ...owner, name: e.target.value })} /></div>
                  <div><Label>Owner login email</Label><Input type="email" value={owner.email} onChange={(e) => setOwner({ ...owner, email: e.target.value })} /></div>
                  <div>
                    <Label>Owner password</Label>
                    <div className="flex gap-2">
                      <Input value={owner.password} onChange={(e) => setOwner({ ...owner, password: e.target.value })} placeholder="Min. 12 characters" />
                      <Button type="button" variant="outline" onClick={() => setOwner({ ...owner, password: generatePassword() })}>Generate</Button>
                    </div>
                    {owner.password ? <p className="mt-1 text-[11px] text-muted-foreground">Share this with the owner — it won't be shown again.</p> : null}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save}>{editing ? "Save" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Sheet open={!!viewing} onOpenChange={(v) => !v && setViewing(null)}>
        <SheetContent className="w-full sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>{viewing?.company} · Members</SheetTitle>
            <SheetDescription>Manage member users under this corporate account.</SheetDescription>
          </SheetHeader>
          <div className="mt-4 space-y-4">
            <div className="rounded-lg border p-3">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Invite member</div>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Full name" value={newMember.name} onChange={(e) => setNewMember({ ...newMember, name: e.target.value })} />
                <Input placeholder="Email" value={newMember.email} onChange={(e) => setNewMember({ ...newMember, email: e.target.value })} />
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <Select value={newMember.role} onValueChange={(v) => setNewMember({ ...newMember, role: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="member">Member</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex gap-2">
                  <Input placeholder="Password (optional)" value={newMember.password} onChange={(e) => setNewMember({ ...newMember, password: e.target.value })} />
                  <Button type="button" variant="outline" onClick={() => setNewMember({ ...newMember, password: generatePassword() })}>Generate</Button>
                </div>
              </div>
              <Button size="sm" className="mt-2 w-full" onClick={addMember}><UserPlus className="mr-1.5 h-4 w-4" /> Invite</Button>
            </div>
            <div className="rounded-lg border p-3">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Add driver to fleet</div>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Full name" value={newDriver.name} onChange={(e) => setNewDriver({ ...newDriver, name: e.target.value })} />
                <Input placeholder="Email" value={newDriver.email} onChange={(e) => setNewDriver({ ...newDriver, email: e.target.value })} />
                <Input placeholder="Phone" value={newDriver.phone} onChange={(e) => setNewDriver({ ...newDriver, phone: e.target.value })} />
                <Button size="sm" onClick={addDriver}><UserPlus className="mr-1.5 h-4 w-4" /> Add driver</Button>
              </div>
            </div>
            <div className="rounded-lg border">
              {viewing?.members.map((m) => (
                <div key={m.id} className="flex items-center gap-3 border-b p-3 last:border-b-0">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-bold">
                    {m.name.split(" ").map((p) => p[0]).slice(0, 2).join("")}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{m.name}</div>
                    <div className="truncate text-xs text-muted-foreground">{m.email}</div>
                  </div>
                  <span className="rounded-full border px-2 py-0.5 text-[10px] font-medium capitalize">{m.role}</span>
                  <Button variant="ghost" size="icon" onClick={() => setDetailMember(m)}><Info className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => openEditMember(m)}><Pencil className="h-3.5 w-3.5" /></Button>
                  {m.role !== "owner" ? (
                    <Button variant="ghost" size="icon" onClick={() => removeMember(m.id)} className="text-destructive hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></Button>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <Dialog open={!!detailMember} onOpenChange={(v) => !v && setDetailMember(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Member details</DialogTitle>
          </DialogHeader>
          {detailMember ? (
            <div className="grid gap-2 text-sm">
              <div><span className="text-muted-foreground">Name:</span> {detailMember.name}</div>
              <div><span className="text-muted-foreground">Email:</span> {detailMember.email}</div>
              <div><span className="text-muted-foreground">Role:</span> <span className="capitalize">{detailMember.role}</span></div>
              <div><span className="text-muted-foreground">Account status:</span> {detailMember.user_status ?? "—"}</div>
              <div><span className="text-muted-foreground">Email verified:</span> {detailMember.email_verified ? "Yes" : "No"}</div>
              <div><span className="text-muted-foreground">Activated:</span> {detailMember.activated ? "Yes" : "No"}</div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailMember(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editMember} onOpenChange={(v) => !v && setEditMember(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Edit member</DialogTitle>
            <DialogDescription>Leave password blank to keep it unchanged.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div><Label>Full name</Label><Input value={editMemberForm.name} onChange={(e) => setEditMemberForm({ ...editMemberForm, name: e.target.value })} /></div>
            <div><Label>Email</Label><Input type="email" value={editMemberForm.email} onChange={(e) => setEditMemberForm({ ...editMemberForm, email: e.target.value })} /></div>
            {editMember?.role !== "owner" ? (
              <div>
                <Label>Role</Label>
                <Select value={editMemberForm.role} onValueChange={(v) => setEditMemberForm({ ...editMemberForm, role: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="member">Member</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : null}
            <div>
              <Label>New password</Label>
              <div className="flex gap-2">
                <Input value={editMemberForm.password} onChange={(e) => setEditMemberForm({ ...editMemberForm, password: e.target.value })} placeholder="Leave blank to keep current" />
                <Button type="button" variant="outline" onClick={() => setEditMemberForm({ ...editMemberForm, password: generatePassword() })}>Generate</Button>
              </div>
              {editMemberForm.password ? <p className="mt-1 text-[11px] text-muted-foreground">Share this with the member — it won't be shown again.</p> : null}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditMember(null)}>Cancel</Button>
            <Button onClick={saveMember}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
